if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      get variantMatrix() {
        if (this.parsedVariantMatrix !== undefined) return this.parsedVariantMatrix;

        try {
          this.parsedVariantMatrix = JSON.parse(this.querySelector('[data-variant-matrix]').textContent);
        } catch (error) {
          this.parsedVariantMatrix = null;
        }

        return this.parsedVariantMatrix;
      }

      get selectedOptionNames() {
        return Array.from(this.querySelectorAll('[data-option-position]')).map((group) => {
          const select = group.querySelector('select');
          return select ? select.value : group.querySelector('input:checked')?.value ?? null;
        });
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      // Changing an earlier option can strand the later ones on a combination that no longer
      // exists, which returns a null variant and strips the price and add-to-cart. Roll the
      // later options forward to the first combination that does exist before requesting it.
      resolveSelection(changedPosition) {
        const matrix = this.variantMatrix;
        if (!matrix || !changedPosition) return null;

        const names = this.selectedOptionNames;
        if (names.some((name) => name === null)) return null;

        for (let index = changedPosition; index < names.length; index++) {
          const matchesPrefix = matrix.combinations.filter((combination) =>
            names.slice(0, index).every((name, position) => combination.options[position] === name)
          );
          if (!matchesPrefix.length) return null;

          if (!matchesPrefix.some((combination) => combination.options[index] === names[index])) {
            const fallback = matchesPrefix.find((combination) => combination.available) || matchesPrefix[0];
            names[index] = fallback.options[index];
          }
        }

        const optionValueIds = names.map((name, index) => matrix.optionValueIds[index]?.[name]);
        return optionValueIds.every(Boolean) ? optionValueIds : null;
      }

      connectedCallback() {
        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);

          const changedPosition = Number(target.closest('[data-option-position]')?.dataset.optionPosition);
          const selectedOptionValues = this.resolveSelection(changedPosition) || this.selectedOptionValues;

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues,
            },
          });
        });
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
